// src/components/employer/Employees.js
import { useState, useEffect } from 'react';
import { Container, Card, Table, Spinner, Alert, Badge, Form, Modal, Row, Col, Button } from 'react-bootstrap';
import { db } from '../../firebase/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const Employees = () => {
  const { userDetails } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtering and sorting states
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Modal states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch employees data
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        if (!userDetails?.communityId) {
          console.log('No community ID available');
          return;
        }

        console.log('Fetching employees for community:', userDetails.communityId);

        // Query users collection for employees in this community
        const usersRef = collection(db, 'users');
        const q = query(
          usersRef,
          where('communityId', '==', userDetails.communityId),
          where('userType', '==', 'employee')
        );

        const querySnapshot = await getDocs(q);
        const employeeData = [];

        // Get availability data for each employee
        for (const doc of querySnapshot.docs) {
          const userData = doc.data();
          
          // Fetch availability data
          const availabilityRef = collection(db, 'availability');
          const availQuery = query(availabilityRef, where('userId', '==', doc.id));
          const availSnapshot = await getDocs(availQuery);
          
          const availability = availSnapshot.empty ? null : availSnapshot.docs[0].data();

          employeeData.push({
            id: doc.id,
            ...userData,
            availability
          });
        }

        console.log('Fetched employee data:', employeeData);
        setEmployees(employeeData);
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Failed to load employees');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, [userDetails]);

  // Filter and sort employees
  const getFilteredEmployees = () => {
    return employees
      .filter(employee => {
        const searchLower = searchTerm.toLowerCase();
        return (
          employee.name?.toLowerCase().includes(searchLower) ||
          employee.email?.toLowerCase().includes(searchLower)
        );
      })
      .sort((a, b) => {
        const aValue = a[sortField] || '';
        const bValue = b[sortField] || '';
        
        let comparison = 0;
        if (sortField === 'createdAt') {
          comparison = new Date(aValue) - new Date(bValue);
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }
        
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  };

  const filteredEmployees = getFilteredEmployees();

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Employees</h4>
          <Badge bg="light" text="dark">
            {filteredEmployees.length} of {employees.length} Employees
          </Badge>
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Search and Filter Section */}
          <Form className="mb-3">
            <Row className="align-items-end">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Search Employees</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Sort By</Form.Label>
                  <Form.Select
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value)}
                  >
                    <option value="name">Name</option>
                    <option value="email">Email</option>
                    <option value="createdAt">Join Date</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button
                  variant="outline-secondary"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                >
                  {sortDirection === 'asc' ? '↑ Ascending' : '↓ Descending'}
                </Button>
              </Col>
            </Row>
          </Form>

          {filteredEmployees.length === 0 ? (
            <Alert variant="info">
              {employees.length === 0 ? 'No employees found.' : 'No employees match your search.'}
            </Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Join Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id}>
                    <td>{employee.name}</td>
                    <td>{employee.email}</td>
                    <td>{new Date(employee.createdAt).toLocaleDateString()}</td>
                    <td>
                      <Badge bg={employee.availability ? 'success' : 'warning'}>
                        {employee.availability ? 'Schedule Set' : 'No Schedule'}
                      </Badge>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                          setSelectedEmployee(employee);
                          setShowModal(true);
                        }}
                      >
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Employee Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Employee Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEmployee && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <h5>Personal Information</h5>
                  <p><strong>Name:</strong> {selectedEmployee.name}</p>
                  <p><strong>Email:</strong> {selectedEmployee.email}</p>
                  <p><strong>Joined:</strong> {new Date(selectedEmployee.createdAt).toLocaleDateString()}</p>
                </Col>
                <Col md={6}>
                  <h5>Status</h5>
                  <Badge bg={selectedEmployee.availability ? 'success' : 'warning'} className="mb-2">
                    {selectedEmployee.availability ? 'Schedule Set' : 'No Schedule'}
                  </Badge>
                </Col>
              </Row>

              <h5>Weekly Availability</h5>
              {selectedEmployee.availability ? (
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>Day</th>
                      <th>Status</th>
                      <th>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(selectedEmployee.availability.availability || {}).map(([day, time]) => (
                      <tr key={day}>
                        <td>{day.charAt(0).toUpperCase() + day.slice(1)}</td>
                        <td>
                          <Badge bg={time.isAvailable ? 'success' : 'secondary'}>
                            {time.isAvailable ? 'Available' : 'Unavailable'}
                          </Badge>
                        </td>
                        <td>
                          {time.isAvailable ? (
                            `${time.startTime} - ${time.endTime}`
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="warning">
                  No availability schedule set
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Employees;